import React, { Children, useEffect, useMemo, useRef, useState } from 'react';
import './coursesDetails.css'
import C2wCourseDetailsCourseDescription from './courseDesription/courseDescription';
import C2wCourseHighLightBar from './contentHightlightBar/courseHighLightBar';
import C2wCompleteSyllabus from './completeSyllabus/completeSyllabus';
import C2wCourseProvided from './courseProvided/courseProvided';
import C2wCourseDemoLectures from './courseDemoLectures/courseDemoLectures';
import C2wCourseHabits from './courseHabits/courseHabits';
import C2wCourseAssignments from './courseAssignments/courseAssignments';
import C2wCourseQuiz from './courseQuiz/courseQuiz';
import C2wCourseCertificate from './courseCertificate/courseCertificate';
import C2wRules from './rules/rules';
import C2wReviews from './reviews/reviews';
import C2wCourseAnalytics from './courseAnalytics/courseAnalytics';
import C2wCourseAnimationBar from './courseAnimatiionBar/courseAnimationBar';
import C2wNavbar from '../../../components/C2wNavbar/navbarIndex';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveContentTabIndex, setPopUpFlag } from '../../../C2wStore/actions';
import { useLocation, useParams } from 'react-router';
import { Element, Helpers } from 'react-scroll';
import Footer from '../Footer';
import { Bounce, toast, ToastContainer } from 'react-toastify';
import C2wAppRegisterInfoPopUp from '../../../common/C2wHome/courseDetails/downlaodAppPopUp';
import C2wAppDownloadPopUp from '../../../common/C2wHome/courseDetails/C2wAppDownloadPopUp';
import { Helmet } from 'react-helmet';
import allCoursesDetails from '../../../data/coursesDetails.json'


function C2wCoursesDetails() {
  const location = useLocation();
  const { slug } = useParams();
  const [commonCourseData, setCourseCommonData] = useState(null);

  const generateSlug = (name) => {
    return name?.replace(/\s+/g, '-') ?? '';
  };

  /**
  * @hook useEffect
  * @author Vishal Lohbande
  * @Date 27-05-2025
  *
  * useEffect - Fetches and processes detailed course data from `/data/coursesDetails.json` whenever `courseId.id` changes.
  *
  * Behavior:
  * - Runs whenever the `courseId.id` value changes.
  * - Fetches the `coursesDetails.json` file from the `/data/` directory.
  * - Parses the response as JSON.
  * - Extracts and sets common course data using `setCourseCommonData`.
  * - Stores the entire data response in `courseDetailData`.
  * - Finds and sets a specific course from the `courses` array by matching its `id` to `courseId.id`.
  *
  * Usage Scenario:
  * - Used on a course detail page to dynamically load course-specific and common content based on the selected course ID.
  * - Ensures fresh data is rendered when users switch between different course pages.
  *
  * Notes:
  * - Assumes `courseId.id` is a valid string or number that can be parsed.
  * - Ensure `coursesDetails.json` exists and is properly structured with `courses` and `CourseDetailsCommon` keys.
  * - Consider adding loading/error state for improved UX.
  */
  // const [courseDetailData, setCourseDetailData] = useState(null)

  // useEffect(() => {
  //   fetch("/data/coursesDetails.json")
  //     .then((res) => res.json())
  //     .then((data) => {
  //       setCourseCommonData(data?.CourseDetailsCommon)
  //       setCourseDetailData(data);
  //       console.log(data)
  //       const course = data?.courses?.find(
  //         (c) => generateSlug(c.courseTitle) === slug
  //       );
  //       setCourseData(course);

  //     })
  //     .catch((err) => {
  //       console.error("Failed to fetch course details:", err);
  //     });
  // }, [slug]);

  const courseData = useMemo(() => {
    return allCoursesDetails?.courses?.find(
      (c) => generateSlug(c.courseTitle) === slug

    );

  }, [slug]);


  /**
 * @hook useEffect
 * @author Vishal Lohbande

 * Description:
 * - Runs once on component mount (empty dependency array).
 * - Finds a course object from `courseDetailData.courses` where the course `id` matches the given `id`.
 * - Sets the found course object into the state variable `courseData` using `setCourseData`.
 *
 * Usage:
 * - Used to initialize or update `courseData` based on the route parameter or passed `id`.
 *
 * Notes:
 * - Assumes `courseDetailData` and `id` are available in the component scope.
 * - No cleanup needed as this runs only once.

 */


  const syllabusRef = useRef(null)
  const providedRef = useRef(null)
  const demoLecturesRef = useRef(null)
  const habitsRef = useRef(null)
  const assignmentsRef = useRef(null)
  const quizRef = useRef(null)
  const analyicsRef = useRef(null)
  const reviewRef = useRef(null)
  const rulesRef = useRef(null)
  const certificateRef = useRef(null)
  const scrollerContainerRef = useRef()

  const dispatch = useDispatch()

  const refSection = [
    syllabusRef,
    providedRef,
    demoLecturesRef,
    assignmentsRef,
    quizRef,
    analyicsRef,
    reviewRef,
    certificateRef,
    rulesRef
  ]

  /**
   * 
   * @author vishal Lohbande 
   * @handleRef - function name

 * handleRef - Handles smooth scrolling to a section and updates the active tab index.
 * @param {number} index ( return active index ) - The index of the section to scroll to. If index is -1, no action is taken.

 * Behavior:
 * - Scrolls the page to the section referenced in `refSection[index]`.
 * - Adds a vertical offset of 70px to account for fixed headers or spacing.
 * - Smoothly animates the scroll into view.
 * - Dispatches an action to update the currently active tab index in the state.
 *
 * Returns:
 * - The index that was scrolled to, or nothing if index is -1.
 */
  const handleRef = (index) => {
    dispatch(setActiveContentTabIndex(index));
    return index;
  };


  /**
 * @hook useEffect
 * @author Vishal Lohbande
 *
 * Description:
 * - Sets up an Intersection Observer to observe visibility of elements referenced in `refSection`.
 * - When an observed element crosses the visibility threshold (50% visible), it finds the element's index in `refSection`.
 * - Dispatches an action `setActiveContentTabIndex` with the active element's index.
 * - Adds observers to all `refSection` elements on mount.
 * - Cleans up by removing observers on component unmount.
 *
 * Usage:
 * - Used to track which section is currently visible on the screen to update UI (e.g., active tabs or navigation highlights).
 *
 * Notes:
 * - Threshold is set to 0.5, meaning the callback triggers when 50% of the element is visible.
 * - Assumes `refSection` is an array of refs, and `dispatch` and `setActiveContentTabIndex` are available in scope.
 * - Empty dependency array means this effect runs only once on mount.
 */



  const [downloadFlag, setDownloadFlag] = useState(false)
  const handleClose = () => {
    console.log("close")
    setDownloadFlag(false)
  }

  /**
 * @constant popupFlag
 * @author Vishal Lohbande
 *
 * Description:
 * - React state boolean to track the visibility of a popup.
 * - Initialized to `false` (popup hidden).
 */

  /**
   * @function handlePopup
   * @author Vishal Lohbande
   *
   * Description:
   * - Toggles the `popupFlag` state to show or hide the popup.
   * - When opening the popup (`popupFlag` was false), it resets the `name` state to an empty string and clears any error state.
   *
   * Usage:
   * - Used as a handler to open or close a popup/modal and reset certain form states on open.
   */
  const popupFlag = useSelector((state) => state.data.popupFlag);
  const handlePopup = () => {
    setError(false)
    dispatch(setPopUpFlag(!popupFlag));


    if (popupFlag === false) {
      setName('')
      setError(false)
      setNumber('')
    }
  }



  const [error, setError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [isValidNumber, setIsValidNumber] = useState(false)

  var stdData = {
    name: name,
    number: number,
    course: 'JAVA',
  }

  /*function to check the provided mobile no is valid or not*/
  /**
 * @function isValidMobile
 * @author Vishal Lohbande
 *
 * Description:
 * - Validates if the input string is either a valid Indian mobile number or a valid email address.
 * - Indian mobile number pattern: exactly 10 digits starting with digits 6-9.
 * - Email pattern: basic general validation for common email formats.
 *
 * Parameters:
 * - `input` (string): The input string to validate.
 *
 * Returns:
 * - `true` if the input matches either the mobile number or email regex pattern.
 * - `false` otherwise.
 *
 * Usage:
 * - Used to validate form input where either a mobile number or email is acceptable.
 */
  const isValidMobile = (input) => {
    const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number pattern (10 digits, starts with 6-9)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // General email pattern

    if (mobileRegex.test(input)) {
      return true;
    } else if (emailRegex.test(input)) {
      return true;
    } else {
      return false;
    }
  };

  /**
 * @function handleName
 * @author Vishal Lohbande
 *
 * Description:
 * - Updates the `name` state with the current value from the input field.
 *
 * Parameters:
 * - `e` (Event): The event object from the input's `onChange` event.
 *
 * Usage:
 * - Used as an event handler for controlled input to update the `name` state dynamically.
 */
  const handleName = (e) => {
    setName(e.target.value)

  }

  /**
 * @function handleNumber
 * @author Vishal Lohbande
 *
 * Description:
 * - Updates the `number` state with the current value from the input field.
 * - If the input length reaches exactly 10 and the `isValidNumber` flag is true, resets `isValidNumber` to false.
 *
 * Parameters:
 * - `e` (Event): The event object from the input's `onChange` event.
 *
 * Usage:
 * - Used as an event handler for controlled input to update the `number` state and manage validation status.
 */
  const handleNumber = (e) => {
    setNumber(e.target.value)
    if (e.target.value.length === 10 && isValidNumber) {
      setIsValidNumber(false)
    }
  }


  const handleError = (error) => {
    setError(error)
  }


  const formatDateTime = (dateTime) => {
    try {
      const date = new Date(dateTime);
      const options = { year: "numeric", month: "2-digit", day: "2-digit" };
      const formattedDateSplit = date.toLocaleDateString("en-GB", options).replace(/\//g, "-").split('-');
      const formattedDate = `${formattedDateSplit[2]}-${formattedDateSplit[1]}-${formattedDateSplit[0]}`
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";
      const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${"00"}`;
      return `${formattedDate} ${formattedTime}`;
    } catch (err) {

    }

  };

  /**
   * @function handleSubmit
   * @author Vishal Lohbande
   *
   * Description:
   * - Handles form submission asynchronously.
   * - Sets success state to true initially.
   * - Prepares `stdData` object with form input values (name, number, and a fixed course value).
   * - Validates if the required fields are non-empty.
   * - Validates the mobile number/email using `isValidMobile`.
   * - If validation fails, sets appropriate error and validation flags and stops execution.
   * - Constructs a `FormData` object with `stdData` and the current date.
   * - Sends a POST request with the form data to a Google Apps Script endpoint.
   * - Handles success by resetting flags and clearing input fields.
   * - Handles errors by resetting flags and closing popup without crashing.
   *
   * Parameters:
   * - `e` (Event): Form submission event (currently unused but usually to prevent default behavior).
   *
   * Usage:
   * - Used as a submit handler to validate and send form data to backend.
   *
   * Notes:
   * - Consider calling `e.preventDefault()` to prevent default form submission.
   * - `name` state reset to `null` after submission, which might cause controlled input warnings; consider resetting to empty string `''`.
   */
  const handleSubmit = async (e) => {
    setIsSuccess(true)
    stdData = {
      name: name,
      number: number,
      course: courseData?.courseTitle,
    }

    const isInvalid = [
      stdData.name,
    ].some(field => field === null || field === '');

    if (isInvalid) {
      setError(true);
      setIsSuccess(false);
      return;
    }

    if (!isValidMobile(stdData.number)) {
      setIsSuccess(false);
      setError(true)
      setIsValidNumber(true)
      return;
    } else {
      setIsValidNumber(false)
    }


    const formData = new FormData();
    formData.append('date', formatDateTime(new Date().toISOString()));
    Object.keys(stdData).forEach(key => {
      formData.append(key, stdData[key]);
    });

    try {

      const response = await fetch('https://script.google.com/macros/s/AKfycbz51NSoWJfpCpVuK_geh-nNKXTcog7UKBOhPJke9xc6cadj2NlxUXz_1dRnvoYgfNPuLA/exec', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Network error or script issue');
      setIsSuccess(false);
      dispatch(setPopUpFlag(false))
      setName(null)
      setIsValidNumber(false)
      setDownloadFlag(true)
      // toast.success('You have registered Successfully', {autoClose: 2000})
    } catch (error) {
      setIsSuccess(false);
      dispatch(setPopUpFlag(false))
      setIsValidNumber(false)
      setDownloadFlag(false)
    }
  };


  /**
 * @constant windowWidth
 * @author Vishal Lohbande
 *
 * Description:
 * - Tracks the current width of the browser window.
 * - Initializes `windowWidth` with `window.innerWidth`.
 * - Uses `useEffect` to listen for window resize events and update the state accordingly.
 * - Cleans up the event listener on component unmount.
 *
 * Usage:
 * - Useful for implementing responsive designs based on screen width.
 *
 * Notes:
 * - Ensure this code runs only on the client-side, as `window` is undefined on the server.
 */
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);

    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  /**
 * @effect IntersectionObserver for Section Visibility
 * @author Vishal Lohbande
 * 
 * Description:
 * - Sets up an `IntersectionObserver` to detect when sections (referenced in `refSection`) intersect with the viewport.
 * - If a section is at least 50% visible (`threshold: 0.5`), and it intersects the root adjusted by `rootMargin`,
 *   the index of that section is determined.
 * - Dispatches `setActiveContentTabIndex(index)` to update the active tab/index in state based on the visible section.
 *
 * Usage:
 * - Ideal for syncing UI elements like tabs, side indicators, or active menus with the scroll position of the user.
 * - Each section to observe should have a corresponding `ref` in the `refSection` array.
 *
 * Observer Options:
 * - `threshold: 0.5`: Triggers when at least 50% of the section is visible.
 * - `rootMargin: "-139px 0px 0px 0px"`: Adjusts the top margin to trigger intersection earlier or later as needed.
 *
 * Cleanup:
 * - On component unmount, all observed elements are unobserved to prevent memory leaks and unnecessary callbacks.
 *
 * Notes:
 * - This effect runs once on mount due to the empty dependency array `[]`.
 * - Assumes `refSection` is a stable array of refs and `dispatch` is from a Redux-like state management system.
 */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = refSection.findIndex(ref => ref.current === entry.target);
            if (index !== -1) {
              dispatch(setActiveContentTabIndex(index));
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "-139px 0px 0px 0px"
      }
    );

    refSection.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      refSection.forEach(ref => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);




  return (

    <div className='C2wCoursesDetails_ctn' ref={scrollerContainerRef}>
      <Helmet>
        {/* 
        Purpose: Defines the page title displayed in search results and browser tabs. 
                 It’s one of the most important SEO elements. 
        */}
        <title>{`${courseData?.courseTitle} Course Details`}</title>

        {/* 
        Purpose: Provides a brief summary of the page content in search results. 
                 While not a direct ranking factor, it influences CTR. 
        */}
        <meta name="description" content="High-quality Course with best Content." />

        {/* Purpose: Controls indexing and link-following behavior. */}
        <meta name="robots" content="index, follow" />

      </Helmet>
      <div className='C2wCoursesDetails_ctn_scroller' id="C2wCourseDetailsContainer" >
        {/*
 
 /**
 * @component ToastContainer Configuration
 * @library react-toastify
 * @author Vishal Lohbande
 * @Date: 26-05-2025
 * Description:
 * - Renders the container for displaying toast notifications.
 * - Configured using `react-toastify` for user feedback like success messages, errors, or alerts.
 * - Positioned at the top-center of the viewport and styled for visibility and user interaction.
 *
 * Props:
 * - `position="top-center"`: Displays the toast in the top-center of the screen.
 * - `autoClose={1000}`: Toast automatically closes after 1000 milliseconds (1 second).
 * - `hideProgressBar={false}`: Progress bar is visible.
 * - `newestOnTop={false}`: Newer toasts are shown below older ones.
 * - `closeOnClick={false}`: Clicking on the toast won't dismiss it.
 * - `rtl={false}`: Left-to-right layout is used.
 * - `pauseOnFocusLoss`: Toast timer pauses when the window/tab loses focus.
 * - `draggable`: Toast can be dismissed by dragging.
 * - `pauseOnHover`: Toast timer pauses on hover.
 * - `theme="light"`: Uses the light theme for toast UI.
 * - `transition={Bounce}`: Applies a bounce animation for toast appearance/disappearance.
 * - `style={{ zIndex: 111 }}`: Ensures the toast appears above most UI elements by controlling stacking order.
 *
 * Usage:
 * - Place `<ToastContainer />` once in your root component or layout.
 * - Use `toast.success()`, `toast.error()`, etc. to trigger toasts globally.
 *
 * Notes:
 * - This configuration is ideal for brief and non-intrusive messages to improve UX.
 */}
        <ToastContainer
          position="top-center"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />
        <C2wNavbar />
        <div>
          {/* {  
          * @component C2wCourseDetailsCourseDescription
          * @author Vishal Lohbande
          * Description:
          * - Renders the `C2wCourseDetailsCourseDescription` component with specific course-related details.
          * - Passes an object (`courseData`) containing course metadata such as:
          *   - `type`: Type of course (e.g., online, offline)
          *   - `fees`: Course fees
          *   - `gst`: GST amount or percentage
          *   - `courseTitle`: Title of the course
          *   - `courseSubTitle`: Subtitle/Tagline of the course
          *   - `admissionDurationInfo`: Information about course admission duration
          * - Also passes the `handlePopup` function as a prop, likely used to trigger a modal or popup interaction.
          *
          * Usage:
          * - Used on a course details page to show structured information about a specific course.
          *
          * Notes:
          * - Ensure `courseData` is available before rendering to avoid passing `undefined` values.
          * - May be useful to add fallback values or conditional rendering if `courseData` is loaded asynchronously.
          } */}
          <C2wCourseDetailsCourseDescription courseData={{
            type: courseData?.type,
            fees: courseData?.fees,
            gst: courseData?.gst,
            courseTitle: courseData?.courseTitle,
            courseSubTitle: courseData?.courseSubTitle,
            admissionDurationInfo: courseData?.admissionDurationInfo,
            courseDescBg: courseData?.courseDescBg,
          }} handlePopup={handlePopup}
          />
        </div>

        {/* 
        * @component C2wCourseAnimationBar
        * @author Vishal Lohbande
        * Description:
        * - Renders an animated course information bar.
        * - Displays key course data such as:
        *   - `type`: Course delivery mode (e.g., online/offline)
        *   - `fees`: Total course fees
        *   - `gst`: GST amount/percentage
        *   - `courseTitle`: Main title of the course
        *   - `courseSubTitle`: Supporting tagline or subtitle
        *   - `admissionDurationInfo`: Duration or timeline for admissions
        * 
        * Props:
        * - `courseData` (object): Contains all metadata about the course.
        * - `handleIndexRef` (function): Used to pass ref index for scroll or animation behavior.
        * - `handlePopup` (function): Triggers a modal or popup, possibly for enrollment or inquiry.
        * 
        * Usage:
        * - Typically placed on a course detail page to persistently show course info in a sticky or animated bar.
        * 
        * Notes:
        * - Ensure `courseData` is not undefined before passing.
        * - Use fallback or conditional rendering for async data if needed.
        */}
        <C2wCourseAnimationBar
          courseData={{
            type: courseData?.type,
            fees: courseData?.fees,
            gst: courseData?.gst,
            courseTitle: courseData?.courseTitle,
            courseSubTitle: courseData?.courseSubTitle,
            admissionDurationInfo: courseData?.admissionDurationInfo,
          }}
          handleIndexRef={handleRef} handlePopup={handlePopup} />

        {/* 
        * @component C2wCourseHighLightBar
        * @author Vishal Lohbande
        * Description:
        * - Displays a highlight bar for course sections, intended for larger screens.
        * - Likely includes quick access or scroll-to features for course content.
        *
        * Props:
        * - `handleIndexRef` (function): Callback to manage scrolling or section indexing.
        *
        * Conditional Rendering:
        * - Only renders when `windowWidth` is greater than 620 (i.e., non-mobile view).
        *
        * Usage:
        * - Typically shown on course detail pages to enhance desktop UX.
        * - Should be hidden on smaller screens to prevent layout clutter.
        */}
        {windowWidth > 620 ?
          (<C2wCourseHighLightBar handleIndexRef={handleRef} />) : null
        }

        {/* 
         * @component C2wCompleteSyllabus
         * @author Vishal Lohbande
         * Description:
         * - Renders the complete syllabus of a course.
         * - Receives structured syllabus data through the `courseData` prop.
         * - Wrapped in a `div` with `ref={syllabusRef}` to allow scrolling or interaction tracking.
         *
         * Props:
         * - `courseData`: An object containing the syllabus content, likely including modules, topics, and structure.
         *
         * Usage:
         * - Displayed on course details or syllabus section pages to inform users about the course curriculum.
         *
         * Notes:
         * - Ensure `courseData?.courseSyallabusComponentData` is populated before rendering to avoid undefined errors.
         * - The `ref={syllabusRef}` is useful for scroll spy features or programmatic navigation.
         */}
        <Element name="section0">
          <div ref={syllabusRef}>
            <C2wCompleteSyllabus type='Online' courseData={courseData?.courseSyallabusComponentData} />
          </div>
        </Element>


        {/* 
         * @component C2wCourseProvided
         * @author Vishal Lohbande
         * Description:
         * - Displays the list or features of what is provided in the course (e.g., certifications, projects, tools, etc.).
         * - The component is wrapped in a `div` using `ref={providedRef}` for programmatic scroll or tracking purposes.
         *
         * Props:
         * - `courseData`: An object (`courseProvidedComponentData`) containing the course provisions or offerings.
         *
         * Usage:
         * - Typically rendered on a course detail page to showcase included benefits or deliverables.
         *
         * Notes:
         * - Ensure `courseData?.courseProvidedComponentData` is available before rendering.
         * - The `providedRef` can be used for scroll spy navigation or smooth scrolling to this section.
         */}
        <Element name="section1">
          <div ref={providedRef}>
            <C2wCourseProvided courseData={courseData?.courseProvidedComponentData} />
          </div>
        </Element>


        {/* 
         * @component C2wCourseDemoLectures
         * @author Vishal Lohbande 
         * Description:
         * - Renders a list or section of demo lecture videos related to the course.
         * - Wrapped in a `div` with `ref={demoLecturesRef}` to allow programmatic scrolling or intersection observation.
         *
         * Props:
         * - `courseData`: An object (`courseVideoContent`) containing video URLs, titles, thumbnails, or other metadata related to demo lectures.
         *
         * Usage:
         * - Typically used on a course details page to showcase sample/demo lectures to prospective students.
         *
         * Notes:
         * - Ensure `courseData?.courseVideoContent` is present before rendering to avoid undefined errors.
         * - `demoLecturesRef` enables scroll tracking or navigation linking to this section.
         */}
        <Element name="section2">
          <div ref={demoLecturesRef}>
            <C2wCourseDemoLectures courseData={courseData?.courseVideoContent} />
          </div>
        </Element>

        {/* 
         * @component C2wCourseHabits and C2wCourseAssignments
         * @author Vishal Lohbande
         * Description:
         * - Wraps two components inside a div with `ref={assignmentsRef}` for scroll or intersection tracking.
         * - `<C2wCourseHabits>` displays course-related habits or practices data.
         * - `<C2wCourseAssignments>` displays assignments related to the course.
         *
         * Props:
         * - `courseData.habitsComponentData`: Data for habits, passed to `C2wCourseHabits`.
         * - `courseData.assignmantComponentData`: Data for assignments, passed to `C2wCourseAssignments`.
         *
         * Usage:
         * - Used within a course details page to show study habits and assignment details.
         *
         * Notes:
         * - Ensure `courseData` is loaded and these properties exist before rendering to avoid errors.
         * - The `assignmentsRef` can be used for smooth scrolling or active section detection.
         */}
        <Element name="section3">
          <div ref={assignmentsRef}>
            <C2wCourseHabits courseData={courseData?.habitsComponentData} />
            <C2wCourseAssignments courseData={courseData?.assignmantComponentData} />
          </div>
        </Element>

        {/* 
        * @component C2wCourseQuiz
        * @author Vishal Lohbande
        * Description:
        * - Renders the quiz section of the course inside a `div` with a `ref` of `quizRef`.
        * - Passes quiz-related data (`quizsComponentData`) from `courseData` as a prop to `C2wCourseQuiz`.
        *
        * Props:
        * - `courseData.quizsComponentData`: Contains the quiz questions, options, and related metadata.
        *
        * Usage:
        * - Used on the course details page to display quizzes associated with the course.
        *
        * Notes:
        * - Ensure `courseData?.quizsComponentData` is defined to avoid runtime errors.
        * - The `quizRef` allows this section to be targeted for scrolling or intersection observation.
        */}
        <Element name="section4">
          <div ref={quizRef}>
            <C2wCourseQuiz courseData={courseData?.quizsComponentData} />
          </div>
        </Element>

        {/*
        * @component C2wCourseAnalytics
        *
        * Description:
        * - Renders the course analytics section inside a `div` with a `ref` of `analyicsRef`.
        * - Displays analytics-related information, such as course progress, statistics, or insights.
        *
        * Props:
        * - None passed explicitly in this snippet.
        *
        * Usage:
        * - Used on the course details page to provide users with analytic insights about the course.
        *
        * Notes:
        * - The `analyicsRef` can be used for scroll tracking or intersection observer to highlight this section.
        * - Ensure the `C2wCourseAnalytics` component handles internal data fetching or receives necessary props elsewhere.
        */ }
        <Element name="section5">
          <div ref={analyicsRef}>
            <C2wCourseAnalytics />
          </div>
        </Element>

        {/* 
         * @component C2wReviews
         * @author Vishal Lohbande
         * Description:
         * - Renders the reviews section of the course inside a `div` with a `ref` of `reviewRef`.
         * - Passes the `reviewsComponentData` from `courseData` as a prop to the `C2wReviews` component.
         *
         * Props:
         * - `courseData.reviewsComponentData`: Contains data related to course reviews such as user feedback, ratings, and comments.
         *
         * Usage:
         * - Used on the course details page to display student or user reviews.
         *
         * Notes:
         * - Ensure `courseData?.reviewsComponentData` is defined to prevent rendering errors.
         * - The `reviewRef` can be used to handle scrolling, lazy loading, or intersection observer events.
         */}
        <Element name="section6">
          <div ref={reviewRef}>
            <C2wReviews courseData={allCoursesDetails?.CourseDetailsCommon?.reviewsComponentData} />
          </div>
        </Element>


        {/* 
         * @component C2wCourseCertificate
         * @author Vishal Lohbande
         * Description:
         * - Renders the course certificate section inside a `div` with a `ref` of `certificateRef`.
         * - Displays information related to the course certificate awarded upon completion.
         *
         * Usage:
         * - Typically used on the course details page to inform users about certificate issuance.
         *
         * Notes:
         * - The `certificateRef` can be used for scroll tracking, navigation, or other UI effects.
         */}
        <Element name="section7">
          <div ref={certificateRef}>
            <C2wCourseCertificate type='Online' />
          </div>
        </Element>

        {/* 
       * @component C2wRules
       * @author Vishal Lohbande
       * Description:
       * - Renders the course rules section inside a `div` with a `ref` of `rulesRef`.
       * - Passes an object with course-related details as the `courseData` prop, including:
       *    - `fees`: The course fee amount.
       *    - `gst`: GST percentage or amount applicable.
       *    - `courseHours`: Total number of hours for the course.
       *    - `courseValidity`: Duration or validity period of the course.
       *
       * Usage:
       * - Used on the course details page to display important course policies, fees, timings, and validity.
       *
       * Notes:
       * - Ensure the referenced `courseData` properties are loaded before rendering to avoid undefined values.
       * - The `rulesRef` can be used for scroll tracking or UI interactions.
       */}
        <Element name="section8">
          <div ref={rulesRef}>
            <C2wRules courseData={{
              fees: courseData?.fees,
              gst: courseData?.gst,
              courseHours: courseData?.courseHours,
              courseValidity: courseData?.courseValidity,
              type: courseData?.type

            }} />
          </div>
        </Element>
        <div className="ADfooter__container">
          <Footer />
        </div>

      </div>

      {/* 
       * @component C2wDownlaodAppPopUp Wrapper
       * @author Vishal Lohbande
       *
       * Description:
       * - Conditionally renders a popup component `C2wDownlaodAppPopUp` when `popupFlag` is true.
       * - The popup likely handles user inputs such as name and number and submits data.
       * - Passes multiple props to control the popup's behavior and state:
       *   - `error`: Flag to show error messages.
       *   - `handleName`: Handler for name input changes.
       *   - `handleNumber`: Handler for number input changes.
       *   - `setPopUpFlag`: Function to toggle popup visibility.
       *   - `handleSubmit`: Function to handle form submission.
       *   - `isSuccess`: Indicates successful form submission.
       *   - `isValidNumber`: Validation flag for number input.
       *
       * Usage:
       * - Used to show a modal popup for downloading an app or similar user interaction.
       *
       * Notes:
       * - Ensure that `popupFlag` is controlled properly to show/hide the popup.
       */}
      {popupFlag &&
        <div className='C2wDownaloadApp_popup'>
          <C2wAppRegisterInfoPopUp
            error={error}
            handleName={handleName}
            handleNumber={handleNumber}
            setPopUpFlag={setPopUpFlag}
            handleSubmit={handleSubmit}
            isSuccess={isSuccess}
            isValidNumber={isValidNumber}
            course={courseData?.courseTitle}
          />
        </div>
      }

      {downloadFlag &&
        <div className='C2wDownaloadApp_popup'>
          <C2wAppDownloadPopUp handleClose={handleClose} />
        </div>
      }
    </div>
  )
}

export default C2wCoursesDetails
